/*
 * Step model
 * ----------
 * Personal step IDs encode the applicant id:
 *   General_Details:<applicantId>
 *   Address_History:<applicantId>
 *   Employment:<applicantId>
 * Joint step IDs are bare strings:
 *   Living_Expenses, Liabilities, Assets, Loan_Requirements, Review
 *
 * buildStepList(applicants) returns the flat ordered list used for both the
 * sidebar groups and Prev/Next navigation.
 */

export const PERSONAL_STEP_KINDS = ['General_Details', 'Address_History', 'Employment'];

const PERSONAL_GROUP = {
  General_Details: { group: 'About you', short: 'About you' },
  Address_History: { group: 'Address history', short: 'Address' },
  Employment: { group: 'Employment', short: 'Employment' },
};

const JOINT_STEPS = [
  { id: 'Living_Expenses', label: 'Living expenses', short: 'Expenses', group: 'Finances' },
  { id: 'Liabilities', label: 'Liabilities & debts', short: 'Liabilities', group: 'Finances' },
  { id: 'Assets', label: 'Assets', short: 'Assets', group: 'Finances' },
  { id: 'Loan_Requirements', label: 'Loan requirements', short: 'Loan needs', group: 'Your loan' },
];

const applicantDisplayName = (applicant, index) => {
  const first = (applicant.given_name || '').trim();
  const last = (applicant.family_name || '').trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;
  return `Applicant ${index + 1}`;
};

export const buildStepList = (applicants) => {
  const steps = [];

  // Walk by section first so the flow is:
  //   About you (applicant 1, applicant 2, ...)
  //   Address history (applicant 1, applicant 2, ...)
  //   Employment (applicant 1, applicant 2, ...)
  // Then the joint sections follow.
  PERSONAL_STEP_KINDS.forEach((kind) => {
    const { group, short } = PERSONAL_GROUP[kind];
    applicants.forEach((applicant, index) => {
      steps.push({
        id: `${kind}:${applicant.id}`,
        kind,
        applicantId: applicant.id,
        applicantIndex: index,
        group,
        label: applicantDisplayName(applicant, index),
        short,
        isPersonal: true,
      });
    });
  });

  JOINT_STEPS.forEach((s) => {
    steps.push({ ...s, isPersonal: false, kind: s.id });
  });

  return steps;
};

export const parseStepId = (stepId) => {
  if (!stepId) return { kind: null, applicantId: null };
  const colonIdx = stepId.indexOf(':');
  if (colonIdx === -1) return { kind: stepId, applicantId: null };
  return {
    kind: stepId.slice(0, colonIdx),
    applicantId: stepId.slice(colonIdx + 1),
  };
};

export const findStepIndex = (steps, stepId) => steps.findIndex((s) => s.id === stepId);

export const groupSteps = (steps) => {
  const out = [];
  let current = null;
  steps.forEach((s, i) => {
    if (!current || current.name !== s.group) {
      current = { name: s.group, steps: [] };
      out.push(current);
    }
    current.steps.push({ ...s, index: i });
  });
  return out;
};

/*
 * Sidebar tree: one entry per category, NOT per (section x applicant).
 *
 *   personal-section: single row in the sidebar with each applicant rendered
 *     as a small clickable sub-label below the section title.
 *   joint-section: single row in the sidebar (joint sections have no
 *     applicant variation, just the step itself).
 *
 * Each entry exposes a default `goToStepId` so clicking the title jumps to
 * the first step of that section.
 */
export const buildSidebarTree = (steps, currentStepId) => {
  const groups = groupSteps(steps);

  return groups.map((group) => {
    const allPersonal = group.steps.every((s) => s.isPersonal);
    const containsCurrent = group.steps.some((s) => s.id === currentStepId);
    const lastIdx = group.steps[group.steps.length - 1].index;
    const firstIdx = group.steps[0].index;
    const currentIdx = findStepIndex(steps, currentStepId);
    const isActive = containsCurrent;
    const isCompleted = !containsCurrent && currentIdx > lastIdx;
    const isUpcoming = currentIdx < firstIdx;

    if (allPersonal) {
      return {
        type: 'personal-section',
        title: group.name,
        firstStepId: group.steps[0].id,
        isActive,
        isCompleted,
        isUpcoming,
        applicants: group.steps.map((s) => ({
          stepId: s.id,
          name: s.label,
          isActive: s.id === currentStepId,
          isCompleted: s.index < currentIdx,
        })),
      };
    }

    return {
      type: 'joint-section',
      title: group.name,
      isActive,
      isCompleted,
      isUpcoming,
      items: group.steps.map((s) => ({
        stepId: s.id,
        label: s.label,
        isActive: s.id === currentStepId,
        isCompleted: s.index < currentIdx,
      })),
    };
  });
};

export { applicantDisplayName };
